/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Box,
  IconButton,
  Paper,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  Modal,
  Button,
  Fade,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import * as turf from '@turf/turf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { dataLayerStyles, apiLayer } from 'constants/fdstyleData';

import ResponseDisplay from './ResponseDisplay';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
    margin: '0 auto',
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
    '& h3': {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.4,
      margin: theme.spacing(2, 0, 1.5),
      color: '#1D2023',
    },
  },
  info: {
    padding: theme.spacing(3),
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    marginLeft: 30,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  detailsContainer: {
    backgroundColor: '#f9fafc',
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  orderDetailsTitle: {
    fontWeight: 700,
    fontSize: '16px',
    color: '#1D2023',
    marginBottom: theme.spacing(2.5),
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '40px',
      height: '3px',
      backgroundColor: theme.palette.primary.main,
    },
  },
  detailItem: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
  },
  label: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#6B7280',
    minWidth: '130px',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
    },
  },
  value: {
    fontSize: '14px',
    color: '#1D2023',
    flex: 1,
    wordBreak: 'break-word',
  },
  subId: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    padding: theme.spacing(0, 1.5),
    borderRadius: '6px',
    fontWeight: 600,
    gap: theme.spacing(1),
    fontSize: '13px',

    '& .MuiSvgIcon-root': {
      fontSize: '16px',
    },
  },
  copyButton: {
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(79, 70, 229, 0.1)',
    },
  },
  copied: {
    color: '#10B981',
  },
  chipLabel: {
    display: 'inline-flex',
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 500,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  responseContainer: {
    marginTop: theme.spacing(4),
    width: '100%',
  },
  responseTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '40px',
      height: '3px',
      backgroundColor: theme.palette.primary.main,
    },
  },
  responseContent: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#F9FAFB',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    border: '1px solid #E5E7EB',
    // maxHeight: '500px',
    // overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CBD5E0',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#F1F5F9',
    },
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  successStatus: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10B981',
  },
  errorStatus: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#EF4444',
  },
  dataGroup: {
    marginBottom: theme.spacing(2),
  },
  dataGroupTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  // Enhanced modal styles
  viewResponseButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    borderRadius: '6px',
    padding: theme.spacing(1, 2),
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease-in-out',
  },
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '800px',
    // maxHeight: '90vh',
    // overflowY: 'auto',
    outline: 'none',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
      maxHeight: '95vh',
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    backgroundColor: '#fff',
    borderBottom: '1px solid #E5E7EB',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& h2': {
      fontSize: '18px',
      fontWeight: 600,
      margin: 0,
    },
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: theme.spacing(3),
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  closeButton: {
    color: '#6B7280',
    '&:hover': {
      backgroundColor: 'rgba(107, 114, 128, 0.1)',
    },
  },
  loadingContainer: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  loadingProgress: {
    width: '100%',
    maxWidth: '400px',
    marginBottom: theme.spacing(2),
  },
  emptyStateContainer: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    minHeight: '200px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '1px dashed #E5E7EB',
  },
}));

const ProcessedFdData = ({
  sentinelImagery = [],
  renderTCI,
  setQueryPageIndex,
  setQueryPageSize,
  queryPageIndex,
  queryPageSize,
  isLoading,
  error,
  fdData,
  subscriptionData,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copied, setCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const initialData = Array.isArray(fdData) && fdData.length > 0 ? fdData : [];
  const { responseJson, responseStatus } = initialData[0]?.props || {};

  const {
    apiType,
    datalayerType,
    createdAt,
    subscriptionId,
    area,
    usedCredits,
  } = initialData[0] || {
    apiType: '',
    datalayerType: '',
    createdAt: '',
    subscriptionId: '',
    area: 0,
    usedCredits: 0,
  };

  let featuresArray = [];
  const areas = [];
  let totalArea = 0;
  // Check if we have a FeatureCollection or a single feature
  if (responseJson) {
    if (responseJson.features) {
      // It's a FeatureCollection
      featuresArray = responseJson.features;
    } else if (responseJson.geometry) {
      // It's a single Feature
      featuresArray = [responseJson];
    }
  }

  featuresArray.forEach((feature) => {
    // If pre-calculated area is provided, use it
    if (feature.properties?.area) {
      areas.push(feature.properties.area / 10000);
    } else if (feature.geometry && feature.geometry.type === 'Polygon') {
      // Otherwise, calculate area using Turf.js
      areas.push(turf.area(feature.geometry) / 10000);
    }
  });
  if (Array.isArray(areas) && areas.length > 0) {
    totalArea = areas.reduce((acc, cur) => acc + cur, 0);
  }
  const requestUrl = subscriptionData?.props?.requestUrl || '';

  const handleCopy = () => {
    navigator.clipboard.writeText(subscriptionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const truncateUrl = (url) => {
    if (!url) return '';
    return url.length > 40 ? `${url.substring(0, 37)}...` : url;
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.info}>
        <div className={classes.detailsContainer}>
          <Typography className={classes.orderDetailsTitle}>
            Order Details
          </Typography>

          <div className={classes.dataGroup}>
            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Order Name</Typography>
              <Typography className={classes.value}>
                {subscriptionData?.name || 'N/A'}
              </Typography>
            </Box>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Reference Code</Typography>
              <Box className={classes.value}>
                <span className={classes.subId}>
                  #{subscriptionId?.slice(-7) || 'N/A'}
                  <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
                    <IconButton
                      size="small"
                      onClick={handleCopy}
                      className={classes.copyButton}
                    >
                      {copied ? (
                        <CheckIcon
                          fontSize="small"
                          className={classes.copied}
                        />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </span>
              </Box>
            </Box>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.dataGroup}>
            <Typography className={classes.dataGroupTitle}>
              API Information
            </Typography>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>API Type</Typography>
              <Typography className={classes.value}>
                <span className={classes.chipLabel}>
                  {apiLayer[apiType]?.text || apiType || 'N/A'}
                </span>
              </Typography>
            </Box>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Data Layer</Typography>
              <Typography className={classes.value}>
                <span className={classes.chipLabel}>
                  {dataLayerStyles[datalayerType]?.text ||
                    datalayerType ||
                    'N/A'}
                </span>
              </Typography>
            </Box>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Request URL</Typography>
              <Typography className={classes.value}>
                {requestUrl ? (
                  <Tooltip title={requestUrl}>
                    <a
                      href={requestUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      {truncateUrl(requestUrl)}
                      <LaunchIcon fontSize="small" />
                    </a>
                  </Tooltip>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Box>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.dataGroup}>
            <Typography className={classes.dataGroupTitle}>
              Order Information
            </Typography>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Created At</Typography>
              <Typography className={classes.value}>
                {formatDate(createdAt) || 'N/A'}
              </Typography>
            </Box>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Area</Typography>
              <Typography className={classes.value}>
                {totalArea ? `${totalArea.toFixed(2)} ha` : 'N/A'}
              </Typography>
            </Box>

            <Box className={classes.detailItem}>
              <Typography className={classes.label}>Credits Used</Typography>
              <Typography className={classes.value}>
                {usedCredits
                  ? `${parseFloat(usedCredits).toFixed(2)} EUR`
                  : 'N/A'}
              </Typography>
            </Box>
          </div>
        </div>

        {responseStatus && (
          <div className={classes.responseContainer}>
            <Box sx={{ marginTop: theme.spacing(4) }}>
              <Divider />
              <Box
                className={classes.responseTitle}
                sx={{ marginTop: theme.spacing(3) }}
              >
                <Typography variant="p" component="p">
                  Response Body
                </Typography>
                <Box
                  className={`${classes.statusBadge} ${
                    responseStatus >= 200 && responseStatus < 300
                      ? classes.successStatus
                      : classes.errorStatus
                  }`}
                >
                  {responseStatus >= 200 && responseStatus < 300
                    ? 'Success'
                    : 'Error'}{' '}
                  {responseStatus}
                </Box>
              </Box>

              <Button
                variant="contained"
                className={classes.viewResponseButton}
                onClick={handleOpenModal}
                startIcon={<VisibilityIcon />}
                fullWidth={isMobile}
              >
                View Response Details
              </Button>

              <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                  timeout: 500,
                }}
                aria-labelledby="response-modal-title"
                aria-describedby="response-modal-description"
              >
                <Fade in={isModalOpen}>
                  <Box className={classes.modalContainer}>
                    <Box className={classes.modalHeader}>
                      <Box className={classes.modalTitle}>
                        <Typography
                          variant="h6"
                          component="h2"
                          id="response-modal-title"
                        >
                          Response Details
                          {responseStatus && (
                            <Box
                              className={`${classes.statusBadge} ${
                                responseStatus >= 200 && responseStatus < 300
                                  ? classes.successStatus
                                  : classes.errorStatus
                              }`}
                              sx={{ ml: 1 }}
                            >
                              {responseStatus >= 200 && responseStatus < 300
                                ? 'Success'
                                : 'Error'}{' '}
                              {responseStatus}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={handleCloseModal}
                        className={classes.closeButton}
                        aria-label="close"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <Box className={classes.modalContent}>
                      {isLoading ? (
                        <Box className={classes.loadingContainer}>
                          <LinearProgress className={classes.loadingProgress} />
                          <Typography variant="body2" color="text.secondary">
                            Loading response data...
                          </Typography>
                        </Box>
                      ) : responseJson ? (
                        <ResponseDisplay
                          responseJson={responseJson}
                          responseStatus={responseStatus}
                        />
                      ) : (
                        <Box className={classes.emptyStateContainer}>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            align="center"
                          >
                            No response data available for this request.
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            The server did not return any response body or the
                            data failed to load.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Fade>
              </Modal>
            </Box>
          </div>
        )}
      </div>
    </Paper>
  );
};

export default ProcessedFdData;
